import type { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import { withQuery } from 'ufo'
import FluentCheckmark16Filled from '~icons/fluent/checkmark-16-filled'
import IcBaselineEdit from '~icons/ic/baseline-edit'
import IcBaselineZoomOutMap from '~icons/ic/baseline-zoom-out-map'
import IcOutlineTagFaces from '~icons/ic/outline-tag-faces'
import IcRoundStars from '~icons/ic/round-stars'
import IconParkOutlineColorFilter from '~icons/icon-park-outline/color-filter'
import MaterialSymbolsPalette from '~icons/material-symbols/palette'
import MaterialSymbolsTune from '~icons/material-symbols/tune'
import MdiFormTextbox from '~icons/mdi/form-textbox'
import RadixIconsFace from '~icons/radix-icons/face'
import RadixIconsHand from '~icons/radix-icons/hand'
import RiVideoAiLine from '~icons/ri/video-ai-line'
import HiresIcon from 'src/assets/icon/hires.svg?react'
import type { EnhancementPlugin } from 'src/components/GenerationTask/constants'
import { INPAINT_MODELS } from 'src/components/GenerationTask/MultiColumnGenerationForm/GenerativeFill/GenerativeFillEditorV2/constants'
import Text from 'src/components/ui/Text'
import { useCopy } from 'src/hooks/useCopy'
import { useEnhancementPlugins } from 'src/services/constants'
import { useInferenceProfileData } from 'src/services/orpc/inferenceProfile'
import { useModelVersion } from 'src/stores/model'
import { cx } from 'src/utils/cx'
import { normalizeModelType } from 'src/utils/model'
import type { ParsedParamsForArtworkDetail } from './parser'

function isMatchScene(
  sceneId: string,
  parsedParams?: ParsedParamsForArtworkDetail,
) {
  if (parsedParams && 'sceneId' in parsedParams) {
    return parsedParams.sceneId === sceneId
  }
  return false
}

function taskAbilityParser(
  parsedParams?: ParsedParamsForArtworkDetail,
  context?: { enhancementPlugins?: Record<string, EnhancementPlugin> },
): TaskCapaibilities {
  if (!parsedParams) return {}

  const isWorkflow = parsedParams.type === 'workflow'
  const showI2vProCapability =
    parsedParams.type === 'i2vPro' || parsedParams.type === 'ref2v'
  const isArtFilter = parsedParams.type === 'art-filter'
  const isAnimeFigure = isMatchScene('anime-figure', parsedParams)
  const isAnimeBadge = isMatchScene('anime-badge', parsedParams)
  const isTarotCard = isMatchScene('tarot-card', parsedParams)

  if (isArtFilter) {
    return { 'art-filter': true }
  } else if (showI2vProCapability) {
    return { i2vpro: true }
  } else if (isWorkflow) {
    const handfix =
      context?.enhancementPlugins?.handfix &&
      parsedParams.workflowName.startsWith(
        context.enhancementPlugins.handfix.workflowName + ':',
      )
    const faceDetailer =
      context?.enhancementPlugins?.['face-detailer'] &&
      parsedParams.workflowName.startsWith(
        context.enhancementPlugins['face-detailer'].workflowName + ':',
      )
    const inpaint = Object.keys(INPAINT_MODELS).some(
      it => parsedParams.workflowName === it,
    )
    return {
      handfix,
      inpaint,
      'face-detailer': faceDetailer,
      'anime-figure': isAnimeFigure,
      'anime-badge': isAnimeBadge,
      'tarot-card': isTarotCard,
    }
  } else {
    const hasFaceFix = parsedParams.enableADetailer ?? false
    const hasHires = (parsedParams.upscale ?? 1) > 1
    const hasEnlarge = (parsedParams.enlarge ?? 1) > 1
    const hasPromptHelper = Boolean(parsedParams.magicPrompts)

    return {
      facefix: hasFaceFix,
      hires: hasHires,
      upscale: hasEnlarge,
      handfix: false,
      'face-detailer': false,
      'prompt-helper': hasPromptHelper,
      'anime-figure': isAnimeFigure,
      'anime-badge': isAnimeBadge,
      'tarot-card': isTarotCard,
    }
  }
}

/**
 * Fields should be kebab-cased for i18n keys
 */
type TaskCapaibilities = {
  'face-detailer'?: boolean
  handfix?: boolean
  facefix?: boolean
  upscale?: boolean
  hires?: boolean
  i2vpro?: boolean
  inpaint?: boolean
  'prompt-helper'?: boolean
  'anime-figure'?: boolean
  'anime-badge'?: boolean
  'tarot-card'?: boolean
  'art-filter'?: boolean
}

const IconMap = {
  'face-detailer': RadixIconsFace,
  handfix: RadixIconsHand,
  upscale: IcBaselineZoomOutMap,
  hires: HiresIcon,
  facefix: IcOutlineTagFaces,
  i2vpro: RiVideoAiLine,
  inpaint: IcBaselineEdit,
  'prompt-helper': MdiFormTextbox,
  'anime-figure': IcOutlineTagFaces,
  'anime-badge': IcOutlineTagFaces,
  'tarot-card': IcRoundStars,
  'art-filter': IconParkOutlineColorFilter,
} satisfies Record<keyof TaskCapaibilities, FC>

/**
 * Which "abilities" the task used to generate
 * @param props
 * @returns
 */
export function ArtworkTaskCapabilityItems(props: {
  parsedParams?: ParsedParamsForArtworkDetail
}) {
  const { data: enhancementPlugins } = useEnhancementPlugins()
  const abilities = taskAbilityParser(props.parsedParams, {
    enhancementPlugins,
  })

  const items = Object.entries(abilities)
    .filter(([key, value]) => value)
    .map(([key]) => {
      const Icon = IconMap[key as keyof TaskCapaibilities]

      const scenes = ['anime-badge', 'tarot-card', 'anime-figure'] as const

      const targetUrl = match(key)
        .with(...scenes, () => `/generator/preset/${key}`)
        .with('i2vpro', () => '/generator/image/animation')
        .with('inpaint', () => '/generator/image/editor')
        .with('art-filter', () => {
          if (props.parsedParams?.type === 'art-filter') {
            return withQuery('/generator/image/enhancement', {
              filterId: props.parsedParams.filterId,
              strength: props.parsedParams.strength,
            })
          }
          return '/generator/image/enhancement'
        })
        .otherwise(() => undefined)

      const i18nKey = match(key)
        .with(...scenes, () => `growth:chat-editing-scene.${key}.name`)
        .otherwise(() => `artwork:parameter.task-capabilities.${key}.label`)

      return (
        <TaskAbility
          key={key}
          icon={<Icon className="size-4" />}
          highlight={key === 'i2vpro'}
          navigateTo={targetUrl}
          label={
            <Text i18nKey={i18nKey}>
              <Text
                i18nKey={`artwork:parameter.task-capabilities.unknown.label`}
              />
            </Text>
          }
        />
      )
    })

  const hasExtraTags =
    (props.parsedParams?.type === 'generator' ||
      props.parsedParams?.type === 't2i2v') &&
    Boolean(
      props.parsedParams.inferenceProfile || props.parsedParams.modelStyle,
    )

  if (items.length < 1 && !hasExtraTags) return null

  return (
    <section className="flex flex-wrap items-center gap-2">
      {(props.parsedParams?.type === 'generator' ||
        props.parsedParams?.type === 't2i2v') && (
        <>
          <InferenceProfileTag
            modelId={props.parsedParams.modelId}
            profileName={props.parsedParams.inferenceProfile}
          />
          <ModelStyleTag
            modelId={props.parsedParams.modelId}
            modelStyle={props.parsedParams.modelStyle}
          />
        </>
      )}
      {items}
    </section>
  )
}

function InferenceProfileTag(props: {
  modelId?: string
  profileName?: string
}) {
  const { t } = useTranslation(['generation'])
  const modelVersion = useModelVersion(props.modelId)
  const { profiles } = useInferenceProfileData(modelVersion?.id)
  const profile = profiles?.find(p => p.profileName === props.profileName)

  if (!props.profileName || !profile) return null

  return (
    <TaskAbility
      icon={<MaterialSymbolsTune className="size-4" />}
      label={
        <>
          {t('generation:inference-profile.label')}: {profile.title}
        </>
      }
    />
  )
}

function ModelStyleTag(props: {
  modelId?: string
  modelStyle?: {
    type: 'key' | 'custom' | 'styleKeyRef'
    keyId?: string
    custom?: string
    refId?: string
  }
}) {
  const { t } = useTranslation(['generation'])
  const [copy, copied] = useCopy()
  const modelVersion = useModelVersion(props.modelId)
  const modelType = normalizeModelType(modelVersion?.model?.type)
  const { modelStyle } = props

  if (!modelStyle) return null

  if (modelStyle.type === 'key' && modelStyle.keyId) {
    const styleName = t([
      `generation:model-style.${modelType}.${modelStyle.keyId}`,
      modelStyle.keyId,
    ])
    return (
      <TaskAbility
        icon={<MaterialSymbolsPalette className="size-4" />}
        label={
          <>
            {t('generation:style.label')}: {styleName}
          </>
        }
      />
    )
  }

  if (modelStyle.type === 'styleKeyRef' && modelStyle.refId) {
    return (
      <button
        className={cx(
          'flex rounded-lg border gap-1 items-center px-2 py-1',
          'border-neutral-500 bg-neutral-100 dark:bg-neutral-800',
          'cursor-pointer hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors',
        )}
        onClick={() => copy(modelStyle.refId!, { enableTrackValue: true })}
      >
        {copied ? (
          <FluentCheckmark16Filled className="size-4 text-green-500" />
        ) : (
          <MaterialSymbolsPalette className="size-4" />
        )}
        <span className="text-sm">
          {t('generation:style.ref-id.label')}: {modelStyle.refId}
        </span>
      </button>
    )
  }

  return null
}

function TaskAbility(props: {
  icon: ReactNode
  label: ReactNode
  highlight?: boolean
  navigateTo?: string
}) {
  const content = (
    <>
      {props.icon}
      <span className="text-sm">{props.label}</span>
    </>
  )

  const className = cx(
    'flex rounded-lg border  gap-1 items-center px-2 py-1',
    props.highlight
      ? 'border-purple-200 dark:bg-purple-500 bg-purple-500 text-white'
      : 'border-neutral-500 bg-neutral-100 dark:bg-neutral-800',
  )

  return props.navigateTo ? (
    <Link to={props.navigateTo} className={className}>
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  )
}
